import { useState } from "react";
import { LinkStep } from "../features/onboarding/LinkStep";
import { CoachLevelStep } from "../features/onboarding/CoachLevelStep";
import { linkStyles } from "../features/onboarding/linkStyles";
import type { LinkResult, LinkSuccess } from "../features/onboarding/types";

interface Props {
  readonly onLink: (username: string, platform: string) => Promise<LinkResult>;
  readonly onDismiss: () => void;
}

/**
 * UsernameLink — modal shown after sign-in to prompt linking a chess account.
 * Two-step flow: LinkStep (enter username) → CoachLevelStep (pick coach level).
 */
export default function UsernameLink({ onLink, onDismiss }: Props) {
  const [linkResult, setLinkResult] = useState<LinkSuccess | null>(null);

  return (
    <div style={linkStyles.overlay}>
      <div style={linkStyles.card}>
        <button style={linkStyles.closeButton} onClick={onDismiss} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div style={linkStyles.iconWrap}>
          <svg
            width="40" height="40" viewBox="0 0 24 24"
            fill="none" stroke="var(--color-accent)" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </div>

        <h2 style={linkStyles.title}>Link your chess account</h2>
        <p style={linkStyles.subtitle}>
          Connect your username to auto-fill games, track stats, and get personalized coaching.
        </p>

        {linkResult ? (
          <CoachLevelStep linkResult={linkResult} onComplete={onDismiss} />
        ) : (
          <LinkStep onLink={onLink} onSuccess={setLinkResult} />
        )}

        <p style={linkStyles.footer}>
          You can change this later in your profile.
        </p>
      </div>
    </div>
  );
}
